// src/components/absen-luar/AbsenLuarPhotoModal.jsx
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, AlertCircle } from 'lucide-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import ShiftList from '../ShiftList';
import Swal from 'sweetalert2';
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import { initDB } from '../../db/db';
import LocationMap from '../LocationMap';
import IzinForm from '../IzinForm';

const SYNC_RETRY_LIMIT = 5;
const SYNC_RETRY_INTERVAL_MS = 5000;
const LOCAL_RIWAYAT_KEY = 'riwayat_absen';
const LOCAL_QUEUE_KEY = 'absen_queue';
const LOCAL_JENIS_IJIN_KEY = 'jenis_ijin_list';
const LOCAL_KARYAWAN_PENGGANTI_KEY = 'karyawan_pengganti_list';

const toMinutes = (timeStr) => {
  const [h = 0, m = 0] = (timeStr ?? '').split(':').map(n => Number(n) || 0);
  return h * 60 + m;
};

export const AbsenLuarPhotoModal = ({
  show,
  onClose,
  reason,
  photo: propPhoto,
  onPhotoChange,
  isSubmitting = false,
  setIsSubmitting,
}) => {
  const [photo, setPhoto] = useState(null);
  const [dinasPurpose, setDinasPurpose] = useState('');
  const [ijinType, setIjinType] = useState('');
  const [replacement, setReplacement] = useState('');
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [keterangan, setKeterangan] = useState('');
  const canvasRef = useRef(null);
  const tomSelectRef = useRef(null);
  const tomSelectInstance = useRef(null);
  const [lokasi, setLokasi] = useState(''); // ← BARU
  const [alamat, setAlamat] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [shiftList, setShiftList] = useState([]);
  const [jumlahHari, setJumlahHari] = useState(''); // string agar bisa kosong
  const [tanggalIzin, setTanggalIzin] = useState([]); // array tanggal izin per hari
  const [tanggalMasukKembali, setTanggalMasukKembali] = useState('');

  // === DATA DARI LOCALSTORAGE (REALTIME) ===
  const [ijinOptions, setIjinOptions] = useState([]);
  const [penggantiList, setPenggantiList] = useState([]);

  useEffect(() => {
    const loadMasterData = () => {
      const ijin = JSON.parse(localStorage.getItem(LOCAL_JENIS_IJIN_KEY) || '[]');
      const pengganti = JSON.parse(localStorage.getItem(LOCAL_KARYAWAN_PENGGANTI_KEY) || '[]');
      setIjinOptions(Array.isArray(ijin) ? ijin : []);
      setPenggantiList(Array.isArray(pengganti) ? pengganti : []);
    };

    loadMasterData();

    const handleStorage = (e) => {
      if ([LOCAL_JENIS_IJIN_KEY, LOCAL_KARYAWAN_PENGGANTI_KEY].includes(e.key)) {
        loadMasterData();
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // === SYNC PHOTO DENGAN PARENT ===
  useEffect(() => {
    if (propPhoto) setPhoto(propPhoto);
  }, [propPhoto]);

  useEffect(() => {
    onPhotoChange?.(photo);
  }, [photo, onPhotoChange]);

  // === UTILS ===
  const getKaryawanId = () => {
    const data = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
    return data.id ? String(data.id) : null;
  };

  // Update tanggal per hari ketika jumlah hari berubah
  const handleJumlahHariChange = (e) => {
    const value = e.target.value;
    setJumlahHari(value);

    if (value === '' || isNaN(value) || value <= 0) {
      setTanggalIzin([]);
      return;
    }

    const jumlah = parseInt(value);
    // Buat array baru dengan panjang sesuai jumlah hari, isi default ''
    setTanggalIzin(Array(jumlah).fill(''));
  };

  // Update tanggal per hari ke-n
  const handleTanggalChange = (index, value) => {
    const newTanggal = [...tanggalIzin];
    newTanggal[index] = value;
    setTanggalIzin(newTanggal);
  };

  const getSelectedShift = () => {
    const saved = localStorage.getItem('shift_terpilih');
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation tidak didukung.', 'error');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const formatted = `${latitude}, ${longitude}`;
        setLokasi(formatted);

        // === PROYEKSI ALAMAT (PAKAI Nominatim - GRATIS) ===
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await res.json();
          const addr = data.display_name?.split(', ').slice(0, 3).join(', ') || 'Lokasi tidak dikenali';
          setAlamat(addr);
        } catch (err) {
          setAlamat('Gagal memuat alamat');
        }

        setGettingLocation(false);
      },
      (error) => {
        setGettingLocation(false);
        Swal.fire('Lokasi Gagal', 'Izin lokasi ditolak atau sinyal lemah.', 'error');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const resetForm = () => {
    setPhoto(null);
    setDinasPurpose('');
    setIjinType('');
    setReplacement('');
    setSignature(null);
    setKeterangan('');
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, 300, 150);
    }
  };

  useEffect(() => {
    if (show) resetForm();
  }, [reason, show]);

  // === TOMSELECT INIT ===
  useEffect(() => {
    if (reason !== 'ijin' || !tomSelectRef.current || tomSelectInstance.current) return;

    const options = penggantiList.map(k => ({
      value: k.id,
      text: k.nama
    }));

    tomSelectInstance.current = new TomSelect(tomSelectRef.current, {
      options,
      maxOptions: 100,
      placeholder: 'Pilih karyawan pengganti',
      searchField: ['text'],
      valueField: 'value',
      labelField: 'text',
      create: false,
    });

    tomSelectInstance.current.on('change', (value) => {
      setReplacement(value);
    });

    return () => {
      if (tomSelectInstance.current) {
        tomSelectInstance.current.destroy();
        tomSelectInstance.current = null;
      }
    };
  }, [reason, penggantiList]);

  // === CANVAS TTD ===
  const startDrawing = useCallback((e, isTouch = false) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e, isTouch = false) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setSignature(canvasRef.current.toDataURL());
  }, [isDrawing]);

  const clearSignature = useCallback(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, 300, 150);
    setSignature(null);
  }, []);

  
  // === TENTUKAN ABSEN MASUK / PULANG ===
  const getAbsenType = useCallback((jamMasuk, jamPulang) => {
    const toMins = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    const now = new Date();
    const nowMins = now.getHours() * 60 + now.getMinutes();

    const masuk = toMins(jamMasuk);
    const pulangRaw = toMins(jamPulang ?? '00:00');

    // Shift malam: pulang < masuk (misal masuk 13:00, pulang 22:00 → bukan malam)
    // atau (masuk 15:00, pulang 00:00 → malam)
    const isShiftMalam = pulangRaw <= masuk;
    const pulang = isShiftMalam ? pulangRaw + 1440 : pulangRaw;

    // Normalisasi: jam 00:00 - 07:00 dianggap masih "malam sebelumnya"
    let nowFix = nowMins;
    if (nowMins < 420 || (isShiftMalam && nowMins < masuk)) {
      nowFix += 1440;
    }

    const BEFORE_PULANG = 270;  // 4.5 jam sebelum pulang boleh klik pulang
    const AFTER_PULANG = 720;   // 12 jam setelah pulang (cover lembur s/d 07:00)

    let isPulangWindow = nowFix >= pulang - BEFORE_PULANG && nowFix <= pulang + AFTER_PULANG;

    // Khusus untuk shift 19:00 - 07:00:
    const isShift19 = jamMasuk?.substring(0, 5) === '19:00' && jamPulang?.substring(0, 5) === '07:00';
    if (isShift19) {
      if (nowMins >= 1020 && nowMins <= 1410) {
        isPulangWindow = false;
      }
    }

    // Khusus untuk shift 07:00 - 19:00:
    const isShift07 = jamMasuk?.substring(0, 5) === '07:00' && jamPulang?.substring(0, 5) === '19:00';
    if (isShift07) {
      if (nowMins >= 300 && nowMins <= 690) {
        isPulangWindow = false;
      }
    }

    return isPulangWindow ? 'Absen Pulang' : 'Absen Masuk';
  }, []);

  // === INDEXEDDB UTILS ===
  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const saveFotoToIndexedDB = async (file, timestamp) => {
    const db = await initDB();
    const blob = await compressImage(file);
    await db.put('fotos', { timestamp, blob }); // SIMPAN BLOB
    return timestamp;
  };

  const getFotoFromIndexedDB = async (timestamp) => {
    const db = await initDB();
    const record = await db.get('fotos', timestamp);
    return record?.blob; // KEMBALIKAN BLOB
  };

  const deleteFotoFromIndexedDB = async (timestamp) => {
    const db = await initDB();
    await db.delete('fotos', timestamp);
  };

  // === SYNC KE SERVER ===
  const updateSyncStatus = useCallback((timestamp, synced) => {
    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
    const updated = riwayat.map(i =>
      i.timestamp === timestamp ? { ...i, synced } : i
    );
    localStorage.setItem(LOCAL_RIWAYAT_KEY, JSON.stringify(updated));
  }, []);

  const removeFromQueue = useCallback((timestamp) => {
    const queue = JSON.parse(localStorage.getItem(LOCAL_QUEUE_KEY) || '[]');
    const filtered = queue.filter(i => i.timestamp !== timestamp);
    localStorage.setItem(LOCAL_QUEUE_KEY, JSON.stringify(filtered));
  }, []);

  const syncAbsenLuar = useCallback(async (item, retryCount = 0) => {
    try {
      const formData = new FormData();
      const fields = ['karyawan_id','absen_id','periode','tanggal','jadwal','lokasi','masuk','pulang','telat','bolos','shift_id','nama','jam_masuk','jam_pulang','uang_makan'];
      fields.forEach(key => {
        if (item[key] !== undefined) formData.append(key, String(item[key] ?? ''));
      });

      if (item.extra) {
        Object.keys(item.extra).forEach(k => {
          if (k !== 'ttd') formData.append(k, item.extra[k] || '');
        });
      }

      if (item.absen_id === 18 && item.foto?.id) {
        const blob = await getFotoFromIndexedDB(item.foto.id);
        if (blob) formData.append('foto', blob, item.foto.name);
      }

      const res = await fetch('https://jne-cilacap.com/slip/public/api/absen', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error(await res.text());

      updateSyncStatus(item.timestamp, true);
      removeFromQueue(item.timestamp);

      if (item.foto?.id) await deleteFotoFromIndexedDB(item.foto.id);

    } catch (err) {
      console.error('SYNC GAGAL:', err.message);
      if (retryCount < SYNC_RETRY_LIMIT) {
        setTimeout(() => syncAbsenLuar(item, retryCount + 1), SYNC_RETRY_INTERVAL_MS);
      } else {
        updateSyncStatus(item.timestamp, 'failed');
      }
    }
  }, [updateSyncStatus, removeFromQueue]);

  // === SYNC OTOMATIS ===
  useEffect(() => {
    const syncUnsynced = async () => {
      if (!navigator.onLine) return;
      const queue = JSON.parse(localStorage.getItem(LOCAL_QUEUE_KEY) || '[]');
      const localHistory = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
      // Pulihkan data Libur lama yang belum pernah dimasukkan ke queue oleh versi sebelumnya.
      localHistory
        .filter(item => Number(item.absen_id) === 7 && item.synced !== true)
        .forEach(item => {
          if (!queue.some(queued => queued.timestamp === item.timestamp)) queue.push(item);
        });
      localStorage.setItem(LOCAL_QUEUE_KEY, JSON.stringify(queue));
      const unsynced = queue.filter(i => !i.synced);
      for (const item of unsynced) await syncAbsenLuar(item);
    };

    syncUnsynced();
    const id = setInterval(syncUnsynced, 10000);
    const handleOnline = () => syncUnsynced();
    window.addEventListener('online', handleOnline);

    return () => {
      clearInterval(id);
      window.removeEventListener('online', handleOnline);
    };
  }, [syncAbsenLuar]);

  // DI ATAS handleSubmit, di dalam komponen
  const isAbsenToday = useCallback((today, absen_id, status) => {
    const riwayat = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
    return riwayat.some(i => {
      if (i.tanggal !== today) return false;
      // Absen_id 14 (cuti) tidak dicek duplikat
      if (absen_id === 14) return false;
      // Absen GPS/lapangan (18): cek berdasarkan absen_id + status (masuk/pulang)
      if (absen_id === 18) return i.absen_id === 18 && i.status === status;
      // Libur (7), dinas (11), dan jenis lain: cek berdasarkan absen_id yang sama pada hari yang sama
      return parseInt(i.absen_id) === parseInt(absen_id);
    });
  }, []);

  const formatLocalDate = (date) => {
    return date.toLocaleDateString('en-CA'); // menghasilkan YYYY-MM-DD versi lokal
  };

  const getTanggalAbsen = () => {
    const now = new Date();
    const jam = now.getHours();
    const jamMasukAngka = selectedShift ? parseInt(selectedShift.jam_masuk) : 0;

    // Mundurkan ke kemarin jika:
    // 1. Dini hari (jam < 7) DAN shift siang/sore/malam (jam masuk >= 13)
    //    → shift 13:00, 15:00, 15:30 yang lembur sampai 06:00-07:00 pagi
    // 2. Atau kondisi lama: jam < 12 dan shift malam (jam masuk >= 18)
    if (jam < 7 && jamMasukAngka >= 13) {
      const kemarin = new Date(now);
      kemarin.setDate(now.getDate() - 1);
      return formatLocalDate(kemarin);
    }

    if (jam < 12 && jamMasukAngka >= 18) {
      const kemarin = new Date(now);
      kemarin.setDate(now.getDate() - 1);
      return formatLocalDate(kemarin);
    }

    return formatLocalDate(now);
  };


  // === HANDLE SUBMIT ===
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const karyawanId = getKaryawanId();
      if (!karyawanId) throw new Error('ID karyawan tidak ditemukan');

      const now = new Date();
      const currentTime = now.toTimeString().slice(0, 8);
      const today = getTanggalAbsen();
      const datetime = today.replace(/-/g, '') + currentTime.replace(/:/g, '');
      const selectedShift = getSelectedShift();

      // Validasi per reason
      if (['lapangan', 'gps error'].includes(reason)) {
        if (!selectedShift) throw new Error('Pilih shift terlebih dahulu!');
        if (!photo) throw new Error('Foto wajib diunggah!');
      }

      // === VALIDASI LOKASI ===
      if (!['libur', 'gps error'].includes(reason) && !lokasi.trim()) {
        throw new Error('Lokasi wajib diambil!');
      }

      if (reason === 'dinas' && !dinasPurpose.trim()) throw new Error('Dalam rangka wajib diisi!');
      if (reason === 'ijin' && (!ijinType || !replacement.trim() || !signature || !keterangan.trim())) {
        throw new Error('Semua field ijin wajib diisi!');
      }

      // DEKLARASI DULU DI LUAR IF BIAR GA ERROR
      let status = 'Absen';
      let isMasuk = true;
      let telat = 0;
      let bolos = 0;
      let uang_makan = 'Rp 0';

      // Baru masuk ke logika kalau alasan lapangan/gps error
      if (['lapangan', 'gps error'].includes(reason) && selectedShift) {
        status = getAbsenType(selectedShift.jam_masuk, selectedShift.jam_pulang);
        isMasuk = status === 'Absen Masuk';

        const jamJadwal = isMasuk ? selectedShift.jam_masuk : selectedShift.jam_pulang;

        // Hitung menit sekarang & jadwal
        const [hNow, mNow] = currentTime.split(':').map(Number);
        const [hJadwal, mJadwal] = jamJadwal.split(':').map(Number);

        const rawMenitNow = hNow * 60 + mNow;
        const menitJadwal = hJadwal * 60 + mJadwal;

        // Untuk absen pulang shift malam: normalisasi jika dini hari
        const masukMinsLuar = toMinutes(selectedShift.jam_masuk);
        const pulangRawLuar = toMinutes(selectedShift.jam_pulang);
        const isShiftMalamLuar = pulangRawLuar <= masukMinsLuar;
        const isCrossoverDayLuar = today !== formatLocalDate(now);
        const menitNow = (!isMasuk && (isShiftMalamLuar || isCrossoverDayLuar) && rawMenitNow < masukMinsLuar)
          ? rawMenitNow + 1440
          : rawMenitNow;
        const menitJadwalNorm = (!isMasuk && isShiftMalamLuar) ? pulangRawLuar + 1440 : menitJadwal;

        // Selalu hitung selisih POSITIF
        const selisihMenit = isMasuk 
          ? menitNow - menitJadwal                    // masuk telat → positif
          : menitJadwalNorm - menitNow;               // pulang cepat → positif

        // Reset dulu
        telat = 0;
        bolos = 0;

        if (isMasuk) {
          telat = selisihMenit > 11 ? selisihMenit : 0;   // toleransi 11 menit masuk
        } else {
          bolos = selisihMenit > 10 ? selisihMenit : 0;   // toleransi 10 menit pulang
        }

        uang_makan = (telat === 0 && bolos === 0) ? 'Rp 12.500' : 'Rp 0';
      }

      const getPeriod = () => {
        let month = now.getMonth()
        let year = now.getFullYear()

        if (now.getDate() >= 21) month += 1

        if (month > 11) {
          month = 0
          year += 1
        }

        return `${new Date(year, month).toLocaleString('en-US', { month: 'long' })} ${year}`
      }

      let fotoData = null;
      if (['lapangan', 'gps error'].includes(reason)) {
        const timestamp = Date.now();
        const fotoId = await saveFotoToIndexedDB(photo, timestamp);
        fotoData = { id: fotoId, name: `absen-${karyawanId}-${datetime}.jpg` };
      }

      const namaKolom = {
        lapangan: fotoData?.name || '',
        'gps error': fotoData?.name || '',
        dinas: dinasPurpose.trim(),
        ijin: keterangan.trim(),
        libur: 'rutin'
      }[reason] || '';

      // DI DALAM handleSubmit, sebelum cek duplikat
      const absen_id = reason === 'ijin'
        ? parseInt(ijinType)
        : { libur: 7, dinas: 11, lapangan: 18, 'gps error': 18 }[reason];

      if (!absen_id) throw new Error('Jenis absen tidak valid');

      // === CEK DUPLIKAT ABSEN ===
      if (isAbsenToday(today, absen_id, status)) {
        const result = await Swal.fire({
          icon: 'warning',
          title: 'Sudah Absen!',
          text: 'Anda sudah absen hari ini.',
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          customClass: { popup: 'swal2-above-modal' }
        });
        if (result.isConfirmed) onClose();
        return;
      }

      const newEntry = {
        karyawan_id: karyawanId,
        absen_id,
        periode: getPeriod(),
        tanggal: today,
        jadwal: selectedShift?.jam_masuk || '',
        lokasi: lokasi,
        masuk: reason === 'ijin' ? replacement : (isMasuk ? currentTime : null),
        pulang: !isMasuk ? currentTime : null,
        telat: String(telat),
        bolos: String(bolos),
        shift_id: selectedShift?.id ? String(selectedShift.id) : '',
        nama: namaKolom,
        jam_masuk: selectedShift?.jam_masuk || '',
        jam_pulang: selectedShift?.jam_pulang || '',
        uang_makan,
        waktu: currentTime,
        status,
        synced: false,
        timestamp: new Date().toISOString(),
        shift_nama: selectedShift?.shift || '',
        foto: fotoData,
        extra: {
          alasan: reason,
          ...(reason === 'dinas' && { keterangan: dinasPurpose }),
          ...(reason === 'ijin' && {
            jenis_ijin: ijinType,
            jumlah_hari: jumlahHari,
            tanggal_ijin: tanggalIzin.join(', '),
            tanggal_masuk: tanggalMasukKembali,
            pengganti: replacement,
            ttd_pemohon: signature
          })
        }
      };

      // Semua jenis absen, termasuk Libur (absen_id 7), wajib masuk riwayat
      // dan antrean agar benar-benar dikirim ke database.
      [LOCAL_RIWAYAT_KEY, LOCAL_QUEUE_KEY].forEach(key => {
        const data = JSON.parse(localStorage.getItem(LOCAL_RIWAYAT_KEY) || '[]');
        const targetData = key === LOCAL_RIWAYAT_KEY
          ? data
          : JSON.parse(localStorage.getItem(key) || '[]');
        targetData.push(newEntry);
        localStorage.setItem(key, JSON.stringify(targetData));
      });

      // Coba kirim langsung; jika gagal, item tetap berada di queue dan akan
      // dicoba ulang oleh sinkronisasi otomatis.
      await syncAbsenLuar(newEntry);

      await Swal.fire({
        icon: 'success',
        title: 'Berhasil!',
        text: `${status} berhasil diproses!`,
        timer: 2000,
        showConfirmButton: false,
      });

      onClose();

    } catch (err) {
      console.error('HANDLE SUBMIT ERROR:', err);
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: err.message || 'Terjadi kesalahan saat absen.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting, setIsSubmitting, reason, photo, dinasPurpose, ijinType, replacement, signature, keterangan,
    getKaryawanId, getSelectedShift, isAbsenToday, getAbsenType, onClose, saveFotoToIndexedDB, syncAbsenLuar
  ]);

  // === VALIDASI & UI ===
  const selectedShift = getSelectedShift();
  const isSubmitDisabled = isSubmitting ||
    (!['libur', 'gps error'].includes(reason) && !lokasi.trim()) ||
    (['lapangan', 'gps error'].includes(reason) && (!photo || !selectedShift)) ||
    (reason === 'dinas' && !dinasPurpose.trim()) ||
    (reason === 'ijin' && (!ijinType || !replacement.trim() || !signature || !keterangan.trim()));

  const getButtonText = () => {
    if (isSubmitting) return 'Mengirim...';
    if (['lapangan', 'gps error'].includes(reason) && selectedShift) {
      return getAbsenType(selectedShift.jam_masuk, selectedShift.jam_pulang);
    }
    return 'Submit Absen';
  };

  const getPendingCount = () => {
    const queue = JSON.parse(localStorage.getItem(LOCAL_QUEUE_KEY) || '[]');
    return queue.filter(i => !i.synced).length;
  };

  let shiftCount = 0;
  try {
    const rawShift = localStorage.getItem("data_shift");
    if (rawShift) {
      const parsed = JSON.parse(rawShift);
      shiftCount = Array.isArray(parsed) ? parsed.length : 0;
    }
  } catch {
    shiftCount = 0;
  }

  const shiftHint = shiftCount > 1 
  ? " ( Geser ➡️ )" 
  : "";

  return (
    <AnimatePresence>
      {show && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 2000 }}
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              borderRadius: '24px 24px 0 0',
              padding: '32px 24px 24px',
              maxHeight: '90vh',
              overflowY: 'auto',
              zIndex: 2001,
              touchAction: isDrawing ? 'none' : 'auto',
            }}
          >
            <button
              onClick={onClose}
              style={{ position: 'absolute', top: 12, left: 16, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <X size={24} color="#666" />
            </button>

            <h3 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: 18, fontWeight: 'bold', color: '#111' }}>
              Absen: <span style={{ textTransform: 'capitalize' }}>{reason}</span>
            </h3>

            {getPendingCount() > 0 && (
              <div style={{
                padding: '10px 16px',
                backgroundColor: '#fef3c7',
                borderRadius: 12,
                fontSize: 14,
                textAlign: 'center',
                marginBottom: 16,
                color: '#92400e'
              }}>
                Menunggu sinkronisasi: {getPendingCount()} data
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              {/* FOTO */}
              {['lapangan', 'gps error'].includes(reason) && (
                <>
                  <p style={{ textAlign: 'center', color: '#666', fontSize: 20, margin: '0 0 20px' }}>
                    “Mohon ambil foto yang memperlihatkan aktivitas pekerjaan Anda, bukan foto selfie.
Pastikan foto menggambarkan apa yang sedang Anda kerjakan.”
                  </p>
                  {/* COLLAPSE CONTOH GAMBAR */}
                    <div
                      style={{
                        marginTop: 20,
                        background: '#f7f7f7',
                        borderRadius: 10,
                        border: '1px solid #e2e2e2',
                        overflow: 'hidden',
                        marginBottom: 20,
                      }}
                    >
                      {/* HEADER COLLAPSE */}
                      <div
                        onClick={() => setShowExample(!showExample)}
                        style={{
                          padding: '12px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ fontWeight: 600, fontSize: 14 }}>
                          Lihat Contoh Foto Aktivitas yang Benar
                        </span>

                        <span style={{ fontSize: 18 }}>
                          {showExample ? '▲' : '▼'}
                        </span>
                      </div>

                      {/* BODY COLLAPSE */}
                      <motion.div
                        initial={false}
                        animate={{
                          height: showExample ? 'auto' : 0,
                          opacity: showExample ? 1 : 0,
                        }}
                        transition={{ duration: 0.3 }}
                        style={{
                          overflow: 'hidden',
                          padding: showExample ? '12px 16px' : '0 16px',
                        }}
                      >
                        <p style={{ marginBottom: 10, fontSize: 14, color: '#555', textAlign: 'center' }}>
                          Contoh foto aktivitas yang benar:
                        </p>

                        {/* Gambar contoh 1 */}
                        <img
                          src="/contoh-aktivitas-1.jpg" // ganti dengan path gambarmu
                          alt="Contoh Aktivitas"
                          style={{
                            width: '100%',
                            borderRadius: 10,
                            marginBottom: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />

                        {/* Gambar contoh 2 (opsional) */}
                        <img
                          src="/contoh-aktivitas-2.jpg" // ganti dengan path gambarmu
                          alt="Contoh Aktivitas"
                          style={{
                            width: '100%',
                            borderRadius: 10,
                            marginBottom: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />

                        {/* Gambar contoh 3 (opsional) */}
                        <img
                          src="/contoh-aktivitas-3.jpg" // ganti dengan path gambarmu
                          alt="Contoh Aktivitas"
                          style={{
                            width: '100%',
                            borderRadius: 10,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          }}
                        />
                      </motion.div>
                    </div>
                  {photo && (
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <img
                        src={URL.createObjectURL(photo)}
                        alt="Bukti"
                        style={{ width: '100%', maxWidth: 300, height: 180, objectFit: 'cover', borderRadius: 16 }}
                      />
                    </div>
                  )}
                  <label style={{
                    display: 'block', width: '93%', backgroundColor: '#003087', color: '#fff',
                    padding: '16px', borderRadius: 16, textAlign: 'center', fontWeight: 'bold',
                    cursor: 'pointer', marginBottom: 20, fontSize: 16,
                  }}>
                    <Camera size={22} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />
                    {photo ? 'Ganti Foto' : 'Ambil Foto'}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={e => e.target.files[0] && setPhoto(e.target.files[0])}
                      style={{ display: 'none' }}
                    />
                  </label>
                </>
              )}

              {/* LIBUR */}
              {reason === 'libur' && (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{
                    width: 80, height: 80, borderRadius: '50%', backgroundColor: '#22c55e',
                    color: '#fff', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px'
                  }}>
                    <AlertCircle size={40} />
                  </div>
                  <p style={{ fontSize: 16, fontWeight: 500, color: '#111' }}>
                    Hari ini saya libur
                  </p>
                </div>
              )}

              {/* DINAS */}
              {reason === 'dinas' && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#111' }}>
                    Dalam Rangka:
                  </label>
                  <input
                    type="text"
                    value={dinasPurpose}
                    onChange={e => setDinasPurpose(e.target.value)}
                    placeholder="Contoh: FGD Operasional di Solo"
                    style={{ width: '93%', padding: '12px', borderRadius: 12, border: '1.5px solid #ddd', fontSize: 15 }}
                  />
                </div>
              )}

              {/* IJIN */}
              {reason === 'ijin' && (
                <>
                  <IzinForm
                    ijinType={ijinType}
                    setIjinType={setIjinType}
                    ijinOptions={ijinOptions}

                    keterangan={keterangan}
                    setKeterangan={setKeterangan}

                    jumlahHari={jumlahHari}
                    handleJumlahHariChange={handleJumlahHariChange}

                    tanggalIzin={tanggalIzin}
                    handleTanggalChange={handleTanggalChange}

                    tanggalMasukKembali={tanggalMasukKembali}
                    setTanggalMasukKembali={setTanggalMasukKembali}

                    tomSelectRef={tomSelectRef}

                    canvasRef={canvasRef}
                    startDrawing={startDrawing}
                    draw={draw}
                    stopDrawing={stopDrawing}
                    clearSignature={clearSignature}
                  />
                </>
              )}

              {/* SHIFT LIST */}
              {['lapangan', 'gps error'].includes(reason) && (
                <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", marginBottom: 8, fontWeight: 500, textAlign: 'center' }}>
                  Pilih Shift {shiftHint} :
                  {selectedShift && (
                    <span style={{ color: "#16a34a", fontSize: 13 }}> ({selectedShift.shift || selectedShift.nama})</span>
                  )}
                </label>

                <div style={{
                  backgroundColor: '#f6fafd', borderRadius: 12, padding: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: '1px solid #d6ecf3', scrollBehavior: 'smooth',
                  scrollbarWidth: 'thin',
                  overflowX: 'auto', gap: 12
                }}>
                  <ShiftList
                    
                    value={selectedShift}
                       
                  />
                </div>
              </div>
              )}

              {/* LOKASI + MAP */}
              {!['libur', 'gps error'].includes(reason) && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Lokasi Saat Ini:
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={lokasi}
                      readOnly
                      placeholder="Tekan tombol untuk ambil lokasi"
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: 12,
                        border: '1.5px solid #ddd',
                        fontSize: 15,
                        backgroundColor: lokasi ? '#f0fdf4' : '#fff',
                      }}
                    />
                    <button
                      onClick={getCurrentLocation}
                      disabled={gettingLocation}
                      style={{
                        padding: '12px 16px',
                        backgroundColor: '#003087',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 12,
                        fontWeight: 'bold',
                        cursor: gettingLocation ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {gettingLocation ? 'Mengambil...' : 'Ambil Lokasi'}
                    </button>
                  </div>

                  {lokasi && (
                    <>
    
                      <LocationMap lat={parseFloat(lokasi.split(',')[0])} lng={parseFloat(lokasi.split(',')[1])} address={alamat} />
                      <p style={{ fontSize: 13, color: '#16a34a', margin: '8px 0 0', fontWeight: 500 }}>
                        Lokasi tersimpan: {lokasi}
                      </p>
                      {alamat && (
                        <p style={{ fontSize: 12, color: '#666', margin: '4px 0', fontStyle: 'italic' }}>
                          {alamat}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TOMBOL */}
              <button
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                style={{
                  width: '100%',
                  backgroundColor: isSubmitDisabled ? '#ccc' : '#22c55e',
                  color: '#fff',
                  padding: '16px',
                  borderRadius: 16,
                  border: 'none',
                  fontWeight: 'bold',
                  cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                  fontSize: 16,
                }}
              >
                {getButtonText()}
              </button>

              <button
                onClick={onClose}
                style={{
                  width: '100%',
                  marginTop: 12,
                  backgroundColor: 'transparent',
                  color: '#666',
                  padding: '14px',
                  border: '1.5px solid #ddd',
                  borderRadius: 16,
                  fontWeight: 'bold',
                  fontSize: 15,
                }}
              >
                Batal
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
